import { useClientTranslation } from '@/lib/core/i18n';
import type { TranslationKey } from '@/lib/core/i18n/types';

/**
 * Local sentinel for "no `eventCategory`" — used as a pill/filter value only, never sent to the
 * API (the server's `eventCategory` filter matches non-null values only; see `matchesCategories`).
 */
export const UNCATEGORISED_EVENT_CATEGORY = '__uncategorised__';

/** Normalizes a raw `eventCategory` value into a stable lookup key — tolerant of the legacy
 * picklist's casing/spacing drift (e.g. "F&B" and "f & b" both resolve to "fb"). */
export function eventCategorySlug(value?: string | null): string | undefined {
  if (!value) return undefined;
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
  return slug || undefined;
}

/** Known picklist values with a designed label — evidenced in `EventBookingRulesTests.cs` and
 * `EventServiceTests.cs` (contract §8 open question: the full picklist is still to be confirmed). */
const CATEGORY_LABEL_KEYS: Record<string, TranslationKey> = {
  fb: 'App.Events.Category.Fb',
  dj: 'App.Events.Category.Dj',
  social: 'App.Events.Category.Social',
};

/** The i18n key for a known category value, or `undefined` when the value isn't mapped yet. */
export function eventCategoryLabelKey(value?: string | null): TranslationKey | undefined {
  const slug = eventCategorySlug(value);
  return slug ? CATEGORY_LABEL_KEYS[slug] : undefined;
}

/**
 * Resolves a raw `eventCategory` to a display label: known value → its i18n label; unknown value
 * → the server string as-is (contract §8: "a APP deve ter fallback para valor desconhecido" — never
 * hide it); absent/blank → the "Other" label.
 */
export function useEventCategoryLabel(): (value?: string | null) => string {
  const t = useClientTranslation();
  return (value?: string | null) => {
    if (!value || value === UNCATEGORISED_EVENT_CATEGORY) return t('App.Events.Category.Other');
    const key = eventCategoryLabelKey(value);
    return key ? t(key) : value;
  };
}
